/** The pager for every server-paged list.
 *
 * It states the position in words - "Showing 101–200 of 4,812" - rather than only offering
 * arrows. That sentence is the entire fix for the finding underneath this component: the
 * console used to request a fixed 200 rows and render them with no pager and no indication
 * that anything older existed, so "who opened this door last Tuesday" was unanswerable once a
 * few hours of watchdog activity had accumulated, and nothing on the screen said so.
 *
 * Offset paging, not cursor paging, on purpose. A person clicking through a log wants to jump,
 * go back, and know how far in they are, and offset gives all three; its weakness - rows
 * shifting by a page as new ones arrive at the top - is tolerable for browsing and is why the
 * API also offers a cursor for exports and investigations that must not skip a row.
 */
interface PagerProps {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  /** Rows on the CURRENT page, so the range shown is what is actually on screen. */
  count: number;
  busy?: boolean;
  onOffsetChange: (offset: number) => void;
  onLimitChange?: (limit: number) => void;
}

const PAGE_SIZES = [25, 50, 100, 200, 500];

export function Pager({
  total,
  limit,
  offset,
  hasMore,
  count,
  busy = false,
  onOffsetChange,
  onLimitChange,
}: PagerProps) {
  const first = total === 0 ? 0 : offset + 1;
  const last = offset + count;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="pager">
      <p className="pager-status" role="status">
        {total === 0 ? (
          "No entries"
        ) : (
          <>
            Showing <strong>{first.toLocaleString()}</strong>–
            <strong>{last.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong>
            <span className="pager-page">
              {" "}
              (page {page.toLocaleString()} of {pages.toLocaleString()})
            </span>
          </>
        )}
      </p>

      <div className="pager-controls">
        {onLimitChange ? (
          <label className="pager-size">
            <span>Per page</span>
            <select
              value={limit}
              disabled={busy}
              onChange={(event) => {
                // Changing the page size while deep in a log would otherwise land the reader
                // somewhere unrelated, so it returns to the first page.
                onLimitChange(Number(event.target.value));
                onOffsetChange(0);
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <button
          type="button"
          className="btn"
          disabled={busy || offset === 0}
          onClick={() => onOffsetChange(0)}
        >
          ⏮ Newest
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || offset === 0}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        >
          ← Newer
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || !hasMore}
          onClick={() => onOffsetChange(offset + limit)}
        >
          Older →
        </button>
      </div>
    </div>
  );
}
